import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, FileText, LogOut, Inbox, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import BlogManager from "@/components/admin/BlogManager";
import ProjectManager from "@/components/admin/ProjectManager";
import { API } from "@/lib/site";

const STATUS = ["baru", "dihubungi", "diproses", "diluluskan", "ditolak"];
const statusColor = {
  baru: "bg-blue-100 text-blue-700",
  dihubungi: "bg-amber-100 text-amber-700",
  diproses: "bg-purple-100 text-purple-700",
  diluluskan: "bg-emerald-100 text-emerald-700",
  ditolak: "bg-red-100 text-red-700",
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("admin_key") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("admin_key"));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, baru: 0 });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("apps");

  const load = useCallback(async (adminKey) => {
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/applications`, { headers: { "x-admin-key": adminKey } }),
        axios.get(`${API}/stats`, { headers: { "x-admin-key": adminKey } }),
      ]);
      setApps(a.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("admin_key");
        setAuthed(false);
        toast.error("Sesi tamat. Sila log masuk semula.");
      }
    }
  }, []);

  useEffect(() => {
    if (authed && key) load(key);
  }, [authed, key, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_key", res.data.key);
      setKey(res.data.key);
      setAuthed(true);
      toast.success("Berjaya log masuk");
    } catch {
      toast.error("Kata laluan tidak sah");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_key");
    setAuthed(false);
    setKey("");
    setPassword("");
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}`, { status }, { headers: { "x-admin-key": key } });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Status dikemas kini");
    } catch {
      toast.error("Gagal mengemas kini status");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A2540] px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0A2540]">
            <Lock className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-5 text-center font-heading text-2xl font-extrabold text-[#0A2540]">Portal Admin</h1>
          <p className="mt-1 text-center text-sm text-slate-500">dausREALTY</p>
          <div className="mt-6">
            <Label htmlFor="pw" className="text-[#0A2540]">Kata Laluan</Label>
            <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Masukkan kata laluan" className="mt-1.5" data-testid="admin-password-input" />
          </div>
          <Button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="mt-5 w-full h-11 bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log Masuk"}
          </Button>
          <a href="/" className="mt-4 block text-center text-xs text-slate-400 hover:text-slate-600">Kembali ke laman utama</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div>
            <p className="font-heading font-extrabold text-[#0A2540]">Portal Admin</p>
            <p className="text-xs text-slate-500">dausREALTY</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => load(key)} data-testid="admin-refresh-btn" className="rounded-full">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-btn" className="rounded-full">
              <LogOut className="h-4 w-4 mr-1" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex gap-2 mb-8">
          <button onClick={() => { setTab("apps"); setSelected(null); }} data-testid="tab-apps"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "apps" ? "bg-[#0A2540] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Permohonan
          </button>
          <button onClick={() => setTab("blog")} data-testid="tab-blog"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "blog" ? "bg-[#0A2540] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Blog
          </button>
          <button onClick={() => setTab("projek")} data-testid="tab-projek"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "projek" ? "bg-[#0A2540] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Projek
          </button>
        </div>

        {tab === "blog" ? (
          <BlogManager adminKey={key} />
        ) : tab === "projek" ? (
          <ProjectManager adminKey={key} />
        ) : (
        <>
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <p className="text-sm text-slate-500">Jumlah Permohonan</p>
            <p className="mt-1 text-3xl font-extrabold text-[#0A2540]" data-testid="stat-total">{stats.total}</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <p className="text-sm text-slate-500">Permohonan Baru</p>
            <p className="mt-1 text-3xl font-extrabold text-[#1D4ED8]" data-testid="stat-baru">{stats.baru}</p>
          </div>
        </div>

        {selected ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-7" data-testid="admin-detail">
            <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A2540] mb-5" data-testid="admin-back-btn">
              <ArrowLeft className="h-4 w-4" /> Kembali ke senarai
            </button>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h2 className="font-heading text-2xl font-bold text-[#0A2540]">{selected.nama}</h2>
                <p className="text-sm text-slate-500">{new Date(selected.created_at).toLocaleString("ms-MY")}</p>
              </div>
              <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                <SelectTrigger className="w-44" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Info label="Telefon" value={selected.telefon} />
              <Info label="E-mel" value={selected.email} />
              <Info label="Jenis Pinjaman" value={selected.jenis_pinjaman} />
              <Info label="Jumlah" value={`RM ${selected.jumlah_pinjaman}`} />
            </div>
            {selected.mesej && <div className="mt-4"><p className="text-xs text-slate-500 uppercase">Mesej</p><p className="mt-1 text-slate-700">{selected.mesej}</p></div>}
            <div className="mt-6">
              <p className="text-xs text-slate-500 uppercase mb-2">Dokumen ({selected.documents?.length || 0})</p>
              {selected.documents?.length ? (
                <div className="space-y-2">
                  {selected.documents.map((d, i) => (
                    <a key={i} href={`${API}/applications/${selected.id}/documents/${i}?key=${encodeURIComponent(key)}`}
                      target="_blank" rel="noopener noreferrer" data-testid={`download-doc-${i}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 hover:border-[#1D4ED8] transition-colors">
                      <span className="flex items-center gap-2 text-slate-700"><FileText className="h-4 w-4 text-[#1D4ED8]" /> {d.filename}</span>
                      <span className="text-xs text-slate-400">{Math.round(d.size / 1024)} KB</span>
                    </a>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">Tiada dokumen dimuat naik</p>}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            {apps.length === 0 ? (
              <div className="py-20 text-center" data-testid="admin-empty">
                <Inbox className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-slate-500">Belum ada permohonan lagi.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {apps.map((a) => (
                  <button key={a.id} onClick={() => setSelected(a)} data-testid={`app-row-${a.id}`}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="font-semibold text-[#0A2540]">{a.nama}</p>
                      <p className="text-sm text-slate-500">{a.jenis_pinjaman} • RM {a.jumlah_pinjaman}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={`capitalize ${statusColor[a.status] || ""}`}>{a.status}</Badge>
                      <span className="text-xs text-slate-400 hidden sm:block">{new Date(a.created_at).toLocaleDateString("ms-MY")}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F8F9FA] px-4 py-3">
      <p className="text-xs text-slate-500 uppercase">{label}</p>
      <p className="mt-0.5 font-medium text-[#0A2540]">{value}</p>
    </div>
  );
}
